// src/App.js
import React, { useState, useEffect } from "react";
import Navbar from "./navbar";
import Footer from "./Footer";
import './App.css';

function App() {
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState(null);
  const [suggestions, setSuggestions] = useState([]); // State for suggestions
  const [isInactive, setIsInactive] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [darkMode, setDarkMode] = useState(true); // Default to dark mode

  // Function to handle search action
  const handleSearch = async () => {
    if (!searchQuery) return;
    try {
      const response = await fetch(`http://127.0.0.1:5000/api/journal?title=${encodeURIComponent(searchQuery)}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setResults(data);
      setIsInactive(data.status === "Inactive");
      setIsActive(data.status === "Active");
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  // Handle input change and fetch suggestions
  const handleInputChange = async (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Fetch suggestions based on the input
    if (query) {
      try {
        const response = await fetch(`http://127.0.0.1:5000/api/journal/suggestions?title=${encodeURIComponent(query)}`);
        if (response.ok) {
          const data = await response.json();
          setSuggestions(data); // Set suggestions based on response
        }
      } catch (error) {
        console.error("Error fetching suggestions:", error);
      }
    } else {
      setSuggestions([]); // Clear suggestions when input is empty
    }
  };

  // Function to handle keypress events (Enter key triggers search)
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div 
      className={`App ${darkMode ? 'bg-[#060415] text-white' : 'bg-white text-black'}`} 
      style={{ 
        backgroundImage: darkMode 
          ? 'linear-gradient(160deg, #060415 0%, #0f1f3a 33%, #0b1332 66%, #0b0b34 100%)' 
          : 'linear-gradient(315deg, #f1f1f1 0%, #ffffff 24%, #e0e2ef 49%, #ffffff 75%, #ffffff 100%)'
      }}
    >
      <Navbar darkMode={darkMode} setDarkMode={setDarkMode} />

      {/* Slogan Section */}
      <div className="bg-cover bg-center h-60 flex items-center justify-center">
        <h1 className={`text-5xl font-bold text-center p-6`}>
          Your Trusted Partner<br />for Genuine Research Platforms and Publishers
        </h1>
      </div>

      {/* Centered Search Section */}
      <div className="flex justify-center items-center mt-10">
        <div className="container mx-auto p-10 relative">
          <h2 className="text-2xl font-bold text-center mb-4">SEARCH FOR A JOURNAL</h2>
          <div className="flex items-center justify-center mt-4 relative">
            <input
              type="text"
              value={searchQuery}
              onChange={handleInputChange} // Use new handler for input change
              onKeyDown={handleKeyDown} // Listen for key press
              placeholder="Enter Journal Name"
              className={`border rounded-md p-3 w-1/2 mr-2 ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-200 text-black'} border-gray-300`}
            />
            <button 
              onClick={handleSearch} 
              className={`rounded-md px-4 py-2 ${darkMode ? 'bg-blue-600 hover:bg-blue-500' : 'bg-blue-500 hover:bg-blue-400'} text-white border border-blue-400`}
            >
              Search
            </button>

            {/* Suggestions Dropdown */}
            {suggestions.length > 0 && (
              <div className={`absolute z-10 top-12 w-1/2 rounded-md shadow-lg ${darkMode ? 'bg-gray-900 text-white' : 'bg-white text-black'}`}>
                {suggestions.slice(0, 6).map((suggestion, index) => (
                  <div 
                    key={index} 
                    className={`p-2 hover:${darkMode ? 'bg-gray-600' : 'bg-gray-200'} cursor-pointer`}
                    onClick={() => {
                      setSearchQuery(suggestion.journal_title);
                      setSuggestions([]);
                    }}
                  >
                    {suggestion.journal_title} ({suggestion.issn})
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Results Section */}
          {results && (
            <div className={`mt-6 p-4 rounded-lg shadow-md ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-black'} border border-gray-300`}>
              <h2 className="text-lg font-bold">Results:</h2>
              <p>Title: {results.journal_title}</p>
              <p>ISSN: {results.issn}</p>
              <p>Publisher: {results.publisher}</p>
              <p>Status: <span className={isActive ? 'text-green-500' : 'text-red-500'}>{results.status}</span></p>
            </div>
          )}
        </div>
      </div>

      {/* ABOUT US Section */}
      <div 
        className="flex justify-center items-center mt-10" 
        style={{ 
          backgroundImage: darkMode 
            ? 'linear-gradient(160deg, #060415 0%, #0f1f3a 33%, #0b1332 66%, #0b0b34 100%)' 
            : 'linear-gradient(315deg, #cfe3ff 0%, #a7c0f9 100%)' 
        }}
      >
        <div className="container mx-auto text-center p-6">
          <h2 className="text-3xl font-bold mb-10">About Us</h2>
          <div className="flex justify-center gap-10">
            <div className="w-1/2 p-6 rounded-lg shadow-lg bg-gray-800 text-white">
              <p className="text-xl leading-relaxed">
                Scholars Friend is a freely accessible platform designed for scholars, researchers, publishers, colleges, and universities. Our platform offers a streamlined search with filters for international journals, publishers, and research papers. We developed this portal to provide researchers with a comprehensive source of genuine information about various journals, complete with relevant indexing details. Given that some journals operate irregularly while others may have good indexing but lack responsiveness, our platform serves two key purposes: it provides essential journal information and connects users with published papers.
              </p>
            </div>
            <div className="w-1/2 flex items-center justify-center">
              <img 
                src="/images/img2.jpg" 
                alt="About Us"
                className={`w-3/4 h-auto rounded-lg shadow-lg`} 
              />
            </div>
          </div>
        </div>
      </div>

      {/* New Section: Understanding Assessors and Publishers in Research */}
      <div className="container mx-auto mt-10 p-6">
        <h2 className={`text-3xl font-bold text-center ${darkMode ? 'text-white' : 'text-black'}`}>Understanding Assessors and Publishers in Research</h2>
        <div className="flex items-center justify-center gap-10 mt-6">
          <div className="w-1/2 p-6 rounded-lg shadow-lg bg-gray-800 text-white">
            <h3 className="text-xl font-bold">Assessors: Ensuring Research Quality</h3>
            <p>
              Assessors evaluate the credibility and quality of journals. They help researchers find trustworthy places to publish.
            </p>
            <ul className="list-disc list-inside mt-2">
              <li>Scopus: High-quality peer-reviewed journals.</li>
              <li>Web of Science: Rigorous journal assessment.</li>
              <li>UGC CARE: Ensures academic standards for Indian scholars.</li>
            </ul>
          </div>
          <img 
            src="/images/IMG3.JPG" 
            alt="Assessors"
            className={`w-1/3 h-auto rounded-lg shadow-lg`} 
          />
        </div>
        <div className="flex items-center justify-center gap-10 mt-6">
          <img 
            src="/images/IMG4.JPG" 
            alt="Publishers"
            className={`w-1/3 h-auto rounded-lg shadow-lg`} 
          />
          <div className="w-1/2 p-6 rounded-lg shadow-lg bg-gray-800 text-white">
            <h3 className="text-xl font-bold">Publishers: The Gatekeepers of Research</h3>
            <p>
              Publishers are responsible for disseminating research. Choose ones that follow ethical standards.
            </p>
            <ul className="list-disc list-inside mt-2">
              <li>Springer: Publishes across disciplines.</li>
              <li>Elsevier: Leading academic journal publisher.</li>
              <li>Wiley: Known for high-quality research publications.</li>
            </ul>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default App;
